import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Stack,
  Divider,
  IconButton,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Snackbar,
  Tooltip,
  Avatar,
  Tabs,
  Tab,
  CardActionArea,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  useMediaQuery,
  tooltipClasses,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Collapse,
  LinearProgress,
  Menu,
} from "@mui/material";
import {
  AutoStories,
  FormatListBulleted,
  Lightbulb,
  ContentCopy,
  Download,
  Save,
  Language,
  EditNote,
  Palette,
  DesignServices,
  Dashboard,
  RocketLaunch,
  Share,
  Close,
  Fullscreen,
  Visibility,
  Delete,
  History,
  Psychology,
  LocalLibrary,
  SmartToy,
  AccessTime,
  Category,
  TextFields,
  Description,
  Image,
  Summarize,
  ViewList,
  PostAdd,
  Refresh,
  ClearAll,
  AutoAwesome,
  AutoFixHigh,
  ArrowForward,
  CheckCircle,
  LockOutlined,
  Send,
  KeyboardDoubleArrowRight,
  PlayArrow,
  AccountTree,
  Link as LinkIcon,
  Headphones,
  ExpandMore,
  ExpandLess,
  LibraryBooks,
  WbSunny,
  NightsStay,
  LightMode,
  WavingHand,
} from "@mui/icons-material";
import Poetlogopen from "../static/POET_Logo_Pen.png";
import { styled } from "@mui/material/styles";
import MuiAlert from "@mui/material/Alert";
import { Breadcrumbs, Link } from "@mui/material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Home, ChevronRight } from "@mui/icons-material";
import newBlogService from "../services/new_blogsServices";
import Loader from "../utils/loader";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// ── Import both Full Book Scenarios + Audiobook ───────────
import FullBookScenario from "./BlogAIFullBookScenario";
import E_BookScenario from "./BlogAI_e-Book";
import BlogAIAudioBook from "./BlogAIAudioBook";

// ─── Greeting Helper ─────────────────────────────────────────────────────────
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12)
    return {
      text: "Good Morning",
      icon: <WbSunny />,
      gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
      bg: "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(251,191,36,0.06))",
      border: "rgba(245,158,11,0.25)",
      emoji: "☀️",
    };
  if (hour >= 12 && hour < 17)
    return {
      text: "Good Afternoon",
      icon: <LightMode />,
      gradient: "linear-gradient(135deg, #3b82f6, #60a5fa)",
      bg: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(96,165,250,0.05))",
      border: "rgba(59,130,246,0.25)",
      emoji: "🌤️",
    };
  if (hour >= 17 && hour < 21)
    return {
      text: "Good Evening",
      icon: <WbSunny sx={{ transform: "rotate(180deg)" }} />,
      gradient: "linear-gradient(135deg, #f97316, #fb923c)",
      bg: "linear-gradient(135deg, rgba(249,115,22,0.1), rgba(251,146,60,0.05))",
      border: "rgba(249,115,22,0.25)",
      emoji: "🌇",
    };
  return {
    text: "Good Night",
    icon: <NightsStay />,
    gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    bg: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.06))",
    border: "rgba(99,102,241,0.25)",
    emoji: "🌙",
  };
};

// ─── Greeting Banner Component ────────────────────────────────────────────────
const GreetingBanner = ({ theme }) => {
  const greeting = getGreeting();
  const [visible, setVisible] = useState(true);
  const hour = new Date().getHours();
  const timeLabel =
    hour >= 5 && hour < 12
      ? "Start your writing day strong!"
      : hour >= 12 && hour < 17
        ? "Keep the creative momentum going!"
        : hour >= 17 && hour < 21
          ? "Evening writing sessions are magical!"
          : "Late-night writers make the best stories!";

  if (!visible) return null;

  return (
    <Fade in timeout={600}>
      <Paper
        sx={{
          mb: 3,
          p: 0,
          borderRadius: 3,
          overflow: "hidden",
          background: greeting.bg,
          border: `1px solid ${greeting.border}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          position: "relative",
        }}
      >
        {/* Colored top bar */}
        <Box sx={{ height: 3, background: greeting.gradient }} />

        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Icon + Text */}
            <Grid xs={12} sm="auto">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  background: greeting.gradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  boxShadow: `0 4px 16px ${greeting.border}`,
                  flexShrink: 0,
                  animation: "greetPulse 2.5s ease-in-out infinite",
                  "@keyframes greetPulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.06)" },
                  },
                }}
              >
                {greeting.icon}
              </Box>
            </Grid>

            <Grid xs={12} sm>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.3 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                >
                  {greeting.emoji} {greeting.text}, Writer!
                </Typography>
                <WavingHand
                  sx={{
                    fontSize: 20,
                    color: "#f59e0b",
                    animation: "wave 1.5s ease-in-out 2",
                    "@keyframes wave": {
                      "0%, 100%": { transform: "rotate(0deg)" },
                      "25%": { transform: "rotate(20deg)" },
                      "75%": { transform: "rotate(-10deg)" },
                    },
                  }}
                />
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.85rem" }}
              >
                {timeLabel} Your AI Book Writing Studio is ready. ✍️
              </Typography>
            </Grid>

            {/* Quick stats / time */}
            <Grid xs={12} sm="auto">
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Chip
                  label={new Date().toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    background: greeting.gradient,
                    color: "white",
                    boxShadow: `0 2px 8px ${greeting.border}`,
                  }}
                />
                <Chip
                  label={new Date().toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    borderColor: greeting.border,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setVisible(false)}
                  sx={{
                    color: "text.disabled",
                    "&:hover": { color: "text.secondary" },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Fade>
  );
};

// ─── Styled Pipeline Connector ────────────────────────────────────────────────
const PipelineConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 22 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: "linear-gradient(135deg, #44AF69, #4A90E2)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08),
    borderRadius: 4,
    transition: "all 0.4s ease",
  },
}));

const extractTitleFromContent = (content) => {
  if (!content) return "";
  const titlePatterns = [
    /(?:^|\n)\s*\*{0,2}title\s*:\*{0,2}\s*(.+)/i,
    /(?:^|\n)\s*book title\s*:\s*(.+)/i,
  ];
  for (const pattern of titlePatterns) {
    const match = content.match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return "";
};

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }) => {
            state.styles.popper.zIndex = 99999;
          },
        },
      ],
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
    color:
      theme.palette.mode === "light"
        ? theme.palette.primary.main
        : theme.palette.common.white,
    boxShadow: theme.shadows[6],
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 10,
    padding: "8px 14px",
    backdropFilter: "blur(6px)",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
  },
}));

// ─── Loading Overlay ──────────────────────────────────────────────────────────
const ModernLoadingOverlay = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha("#0f172a", 0.95)} 0%, ${alpha("#1e293b", 0.95)} 100%)`
            : `linear-gradient(135deg, ${alpha("#f8fafc", 0.95)} 0%, ${alpha("#f1f5f9", 0.95)} 100%)`,
        backdropFilter: "blur(10px)",
      }}
    >
      <Grid container spacing={3} justifyContent="center" alignItems="center">
        <Grid xs={12}>
          <Box
            sx={{
              position: "relative",
              width: 280,
              height: 280,
              margin: "0 auto",
              borderRadius: "50%",
              background:
                "linear-gradient(45deg, #4A90E2, #9C27B0, #FF6B35, #44AF69)",
              backgroundSize: "400% 400%",
              animation: "gradientShift 3s ease infinite",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 10,
                borderRadius: "50%",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #0f172a, #1e293b)"
                    : "linear-gradient(135deg, #ffffff, #f8fafc)",
                zIndex: 1,
              },
              "@keyframes gradientShift": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
              <CircularProgress
                size={120}
                thickness={2}
                sx={{
                  color: "transparent",
                  background:
                    "conic-gradient(from 0deg, transparent 0%, #4A90E2 25%, #9C27B0 50%, #FF6B35 75%, transparent 100%)",
                  borderRadius: "50%",
                  animation: "rotate 2s linear infinite",
                  "@keyframes rotate": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #1e293b, #334155)"
                      : "linear-gradient(135deg, #ffffff, #f1f5f9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "translate(-50%, -50%) scale(1)" },
                    "50%": { transform: "translate(-50%, -50%) scale(1.05)" },
                  },
                }}
              >
                <AutoStories sx={{ fontSize: 28, color: "primary.main" }} />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid xs={12}>
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #60a5fa, #a78bfa)"
                    : "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
              }}
            >
              Crafting Your Book
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", maxWidth: 400, mx: "auto", mb: 3 }}
            >
              AI is generating your content. This may take a few moments...
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Send-to-Next Dialog ──────────────────────────────────────────────────────
const SendToNextDialog = ({
  open,
  onClose,
  fromGenerator,
  toGenerator,
  content,
  onConfirm,
  generators,
}) => {
  const theme = useTheme();
  const fromGen = generators.find((g) => g.id === fromGenerator);
  const toGen = generators.find((g) => g.id === toGenerator);
  if (!fromGen || !toGen) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(toGen.color, 0.2)}`,
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${alpha(fromGen.color, 0.15)}, ${alpha(toGen.color, 0.15)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: fromGen.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                mx: "auto",
                mb: 0.5,
              }}
            >
              {fromGen.icon}
            </Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {fromGen.label}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                height: 2,
                width: "100%",
                background: `linear-gradient(90deg, ${fromGen.color}, ${toGen.color})`,
                borderRadius: 1,
              }}
            />
            <KeyboardDoubleArrowRight
              sx={{
                color: toGen.color,
                fontSize: 28,
                animation: "slideRight 1.2s ease-in-out infinite",
                "@keyframes slideRight": {
                  "0%, 100%": { transform: "translateX(-4px)", opacity: 0.6 },
                  "50%": { transform: "translateX(4px)", opacity: 1 },
                },
              }}
            />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: toGen.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                mx: "auto",
                mb: 0.5,
                boxShadow: `0 4px 16px ${alpha(toGen.color, 0.4)}`,
              }}
            >
              {toGen.icon}
            </Box>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: toGen.color }}
            >
              {toGen.label}
            </Typography>
          </Box>
        </Stack>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ mt: 2, textAlign: "center" }}
        >
          Send to {toGen.label}?
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          Your {fromGen.label.toLowerCase()} content will be pre-filled into{" "}
          {toGen.label} as context.
        </Typography>
      </Box>
      <DialogContent sx={{ py: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ mb: 1, display: "block" }}
        >
          CONTENT PREVIEW
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: alpha(fromGen.color, 0.05),
            border: `1px solid ${alpha(fromGen.color, 0.15)}`,
            maxHeight: 160,
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: "0.82rem",
            lineHeight: 1.6,
            color: "text.secondary",
            whiteSpace: "pre-wrap",
          }}
        >
          {content?.substring(0, 400)}
          {content?.length > 400 ? "..." : ""}
        </Box>
        <Alert
          severity="info"
          sx={{
            mt: 2,
            borderRadius: 2,
            background: alpha(toGen.color, 0.08),
            border: `1px solid ${alpha(toGen.color, 0.2)}`,
            color: "text.primary",
            "& .MuiAlert-icon": { color: toGen.color },
          }}
        >
          This will switch you to <strong>{toGen.label}</strong> and
          auto-populate the relevant fields.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          startIcon={<Send />}
          sx={{
            borderRadius: 2,
            flex: 2,
            background: toGen.gradient,
            boxShadow: `0 4px 16px ${alpha(toGen.color, 0.3)}`,
            "&:hover": { boxShadow: `0 6px 20px ${alpha(toGen.color, 0.45)}` },
          }}
        >
          Send to {toGen.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Pipeline Progress Bar ────────────────────────────────────────────────────
const BookPipelineBar = ({
  generators,
  pipelineContents,
  activeGenerator,
  onStepClick,
  onSendToNext,
  onOpenFullBookV1,
  onOpenFullBookV2,
  onOpenAudioBook,
  onOpenMovieScript,
  theme,
}) => {
  const pipelineOrder = ["idea", "outline", "cover", "summary"];
  const completedSteps = pipelineOrder.filter((id) => !!pipelineContents[id]);
  const progressPct = (completedSteps.length / pipelineOrder.length) * 100;

  // "View Full Book" menu anchor
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        mb: 3,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #1e293b, #0f172a)"
            : "linear-gradient(135deg, #ffffff, #f8fafc)",
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        overflow: "visible",
        position: "relative",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountTree sx={{ color: "white", fontSize: 17 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Book Journey Pipeline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {completedSteps.length}/{pipelineOrder.length} stages completed
            </Typography>
          </Box>
        </Stack>
        <Chip
          label={`${Math.round(progressPct)}%`}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            background:
              progressPct === 100
                ? "linear-gradient(135deg, #44AF69, #2E8B57)"
                : "linear-gradient(135deg, #4A90E2, #9C27B0)",
            color: "white",
            boxShadow:
              progressPct === 100
                ? "0 2px 8px rgba(68,175,105,0.4)"
                : "0 2px 8px rgba(74,144,226,0.3)",
          }}
        />
      </Stack>

      <LinearProgress
        variant="determinate"
        value={progressPct}
        sx={{
          mb: 3,
          height: 5,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.divider, 0.15),
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            background:
              progressPct === 100
                ? "linear-gradient(90deg, #44AF69, #4A90E2)"
                : "linear-gradient(90deg, #4A90E2, #9C27B0)",
            transition: "transform 0.6s ease",
          },
        }}
      />

      {/* Steps */}
      <Grid container spacing={1.5} alignItems="stretch">
        {pipelineOrder.map((stepId, index) => {
          const gen = generators.find((g) => g.id === stepId);
          const isActive = activeGenerator === stepId;
          const isCompleted = !!pipelineContents[stepId];
          const isLocked =
            index > 0 &&
            !pipelineContents[pipelineOrder[index - 1]] &&
            !isCompleted;
          const nextId = pipelineOrder[index + 1];
          const canSendNext = isCompleted && nextId;
          return (
            <Grid xs={12} sm={6} md={3} key={stepId} sx={{ display: "flex" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Card
                  onClick={() => !isLocked && onStepClick(stepId)}
                  sx={{
                    flex: 1,
                    borderRadius: 2.5,
                    cursor: isLocked ? "not-allowed" : "pointer",
                    border: isActive
                      ? `2px solid ${gen.color}`
                      : isCompleted
                        ? `2px solid ${alpha(gen.color, 0.4)}`
                        : `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                    background: isActive
                      ? alpha(gen.color, 0.08)
                      : isCompleted
                        ? alpha(gen.color, 0.04)
                        : isLocked
                          ? alpha(theme.palette.divider, 0.03)
                          : "transparent",
                    opacity: isLocked ? 0.5 : 1,
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: isActive
                      ? `0 4px 16px ${alpha(gen.color, 0.2)}`
                      : "none",
                    "&:hover": !isLocked
                      ? {
                          transform: "translateY(-2px)",
                          boxShadow: `0 6px 20px ${alpha(gen.color, 0.2)}`,
                          borderColor: gen.color,
                        }
                      : {},
                    "&:before": isActive
                      ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: gen.gradient,
                        }
                      : {},
                  }}
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          background:
                            isCompleted || isActive
                              ? gen.gradient
                              : alpha(theme.palette.divider, 0.2),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color:
                            isCompleted || isActive ? "white" : "text.disabled",
                          flexShrink: 0,
                        }}
                      >
                        {isCompleted ? (
                          <CheckCircle sx={{ fontSize: 20 }} />
                        ) : isLocked ? (
                          <LockOutlined sx={{ fontSize: 18 }} />
                        ) : (
                          React.cloneElement(gen.icon, {
                            style: { fontSize: 18 },
                          })
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: isActive
                              ? gen.color
                              : isCompleted
                                ? gen.color
                                : "text.secondary",
                            display: "block",
                            lineHeight: 1.2,
                          }}
                        >
                          Step {index + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color:
                              isActive || isCompleted
                                ? "text.primary"
                                : "text.disabled",
                            fontSize: "0.8rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {gen.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: isCompleted
                            ? "#44AF69"
                            : isActive
                              ? gen.color
                              : alpha(theme.palette.divider, 0.4),
                          boxShadow: isActive
                            ? `0 0 6px ${gen.color}`
                            : isCompleted
                              ? "0 0 6px #44AF69"
                              : "none",
                          animation: isActive
                            ? "blink 1.5s ease-in-out infinite"
                            : "none",
                          "@keyframes blink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.3 },
                          },
                        }}
                      />
                    </Stack>
                    {isCompleted && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 0.8,
                          borderRadius: 1,
                          background: alpha(gen.color, 0.06),
                          border: `1px solid ${alpha(gen.color, 0.12)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontSize: "0.7rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {pipelineContents[stepId]?.substring(0, 80)}...
                        </Typography>
                      </Box>
                    )}
                    {canSendNext && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          <Send sx={{ fontSize: "13px !important" }} />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onSendToNext(stepId, nextId);
                        }}
                        sx={{
                          mt: 1,
                          width: "100%",
                          py: 0.4,
                          fontSize: "0.7rem",
                          borderRadius: 1.5,
                          background: generators.find((g) => g.id === nextId)
                            ?.gradient,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        → {generators.find((g) => g.id === nextId)?.label}
                      </Button>
                    )}
                  </CardContent>
                </Card>
                {index < pipelineOrder.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ArrowForward
                      sx={{
                        fontSize: 18,
                        color: isCompleted
                          ? gen.color
                          : alpha(theme.palette.divider, 0.5),
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {/* Movie Script Button */}
        <Button
          variant="outlined"
          startIcon={<PlayArrow />}
          onClick={onOpenMovieScript}
          sx={{
            width: { xs: "100%", sm: "auto" },
            borderRadius: 2,
            borderColor: "#f59e0b",
            color: "#f59e0b",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": {
              borderColor: "#d97706",
              background: alpha("#f59e0b", 0.06),
            },
          }}
        >
          Movie Script
          <Chip
            label="Soon"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "0.62rem",
              fontWeight: 700,
              background: "linear-gradient(135deg, #f59e0b, #ec4899)",
              color: "white",
            }}
          />
        </Button>
        {/* Audiobook Button */}
        <Button
          variant="outlined"
          startIcon={<Headphones />}
          onClick={onOpenAudioBook}
          disabled={completedSteps.length === 0}
          sx={{
            width: { xs: "100%", sm: "auto" },
            borderRadius: 2,
            borderColor: "#ec4899",
            color: "#ec4899",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": {
              borderColor: "#db2777",
              background: alpha("#ec4899", 0.06),
            },
          }}
        >
          Audiobook Script
        </Button>

        {/* View Full Book — dropdown for V1 and V2 */}
        <Button
          variant="contained"
          startIcon={<AutoStories />}
          endIcon={<ExpandMore />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={completedSteps.length === 0}
          sx={{
            width: { xs: "100%", sm: "auto" },
            borderRadius: 2,
            background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
            boxShadow: "0 4px 16px rgba(74,144,226,0.3)",
            textTransform: "none",
            fontWeight: 700,
            animation:
              completedSteps.length > 0
                ? "glow 2s ease-in-out infinite"
                : "none",
            "@keyframes glow": {
              "0%,100%": { boxShadow: "0 4px 16px rgba(74,144,226,0.3)" },
              "50%": { boxShadow: "0 6px 28px rgba(156,39,176,0.5)" },
            },
          }}
        >
          View Full Book
        </Button>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              mt: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpenFullBookV1();
            }}
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1.5,
              "&:hover": { background: alpha("#4A90E2", 0.06) },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LibraryBooks sx={{ color: "white", fontSize: 16 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Pipeline View
              </Typography>
              <Typography variant="caption" color="text.secondary">
                See all 4 stages compiled (fast)
              </Typography>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpenFullBookV2();
            }}
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1.5,
              "&:hover": { background: alpha("#f59e0b", 0.06) },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #f59e0b, #ec4899)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoStories sx={{ color: "white", fontSize: 16 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                AI E-Book Generator
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Generate full chapters with AI (takes time)
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    </Paper>
  );
};

// ─── Movie Script Coming Soon Dialog ─────────────────────────────────────────
const MovieScriptComingSoon = ({ open, onClose, theme }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha("#f59e0b", 0.25)}`,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #1e293b, #0f172a)"
            : "linear-gradient(135deg, #ffffff, #fefce8)",
      },
    }}
  >
    {/* Colored top bar */}
    <Box
      sx={{
        height: 4,
        background: "linear-gradient(90deg, #f59e0b, #ec4899, #8b5cf6)",
      }}
    />

    <DialogContent sx={{ textAlign: "center", pt: 5, pb: 3, px: 4 }}>
      {/* Animated icon */}
      <Box
        sx={{
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #f59e0b, #ec4899)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
          boxShadow: "0 8px 32px rgba(245,158,11,0.35)",
          animation: "bounce 2s ease-in-out infinite",
          "@keyframes bounce": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-8px)" },
          },
        }}
      >
        <PlayArrow sx={{ fontSize: 44, color: "white" }} />
      </Box>

      {/* Badge */}
      <Chip
        label="Coming Soon"
        size="small"
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: "0.72rem",
          letterSpacing: 1.2,
          background: "linear-gradient(135deg, #f59e0b, #ec4899)",
          color: "white",
          boxShadow: "0 2px 10px rgba(245,158,11,0.3)",
        }}
      />

      <Typography
        variant="h5"
        fontWeight={800}
        sx={{
          mb: 1.5,
          background: "linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        Movie Script Generator
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 360, mx: "auto", mb: 3, lineHeight: 1.8 }}
      >
        We're crafting a powerful AI-driven screenplay tool — write full movie
        scripts with scenes, dialogue, and character arcs. Stay tuned! 🎬
      </Typography>

      {/* Feature previews */}
      <Grid container spacing={1.5} sx={{ mb: 3, textAlign: "left" }}>
        {[
          { icon: "🎭", label: "Scene-by-scene breakdown" },
          { icon: "💬", label: "Smart dialogue generation" },
          { icon: "🎬", label: "Multi-genre screenplay styles" },
          { icon: "🧑‍🤝‍🧑", label: "Character arc builder" },
        ].map((feat) => (
          <Grid xs={6} key={feat.label}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.2,
                borderRadius: 2,
                background: alpha("#f59e0b", 0.07),
                border: `1px solid ${alpha("#f59e0b", 0.18)}`,
              }}
            >
              <Typography sx={{ fontSize: 16 }}>{feat.icon}</Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ lineHeight: 1.3 }}
              >
                {feat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: "block", mb: 1 }}
      >
        We'll notify you when it's ready ✨
      </Typography>
    </DialogContent>

    <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1.5 }}>
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          px: 3,
          textTransform: "none",
          fontWeight: 600,
          borderColor: alpha("#f59e0b", 0.4),
          color: "#f59e0b",
          "&:hover": {
            borderColor: "#f59e0b",
            background: alpha("#f59e0b", 0.06),
          },
        }}
      >
        Got it!
      </Button>
      <Button
        variant="contained"
        onClick={onClose}
        sx={{
          borderRadius: 2.5,
          px: 3,
          textTransform: "none",
          fontWeight: 700,
          background: "linear-gradient(135deg, #f59e0b, #ec4899)",
          boxShadow: "0 4px 16px rgba(245,158,11,0.3)",
          "&:hover": { boxShadow: "0 6px 20px rgba(245,158,11,0.45)" },
        }}
        startIcon={<AutoAwesome />}
      >
        Notify Me
      </Button>
    </DialogActions>
  </Dialog>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const AIBookWriting = () => {
  const createEmptyStageFormData = () => ({
    idea: {},
    outline: {},
    cover: {},
    summary: {},
  });

  const [activeGenerator, setActiveGenerator] = useState("idea");
  const [language, setLanguage] = useState("english");
  const [bookType, setBookType] = useState("");
  const [formData, setFormData] = useState({});
  const [stageFormData, setStageFormData] = useState(createEmptyStageFormData);
  const [loading, setLoading] = useState(false);
  const [generatorLoading, setGeneratorLoading] = useState(null);
  const [generatedContent, setGeneratedContent] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [formattedContent, setFormattedContent] = useState(null);
  const [expandedOutlineChapters, setExpandedOutlineChapters] = useState({});
  const [expandedCoverIdeas, setExpandedCoverIdeas] = useState({});
  const [savedContents, setSavedContents] = useState([]);
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [openSavedDialog, setOpenSavedDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Full book dialog states
  const [openFullBookV1, setOpenFullBookV1] = useState(false);
  const [openFullBookV2, setOpenFullBookV2] = useState(false);
  const [openAudioBook, setOpenAudioBook] = useState(false);

  const [pipelineContents, setPipelineContents] = useState({
    idea: "",
    outline: "",
    cover: "",
    summary: "",
  });
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState({ from: null, to: null });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const [openMovieScript, setOpenMovieScript] = useState(false);

  const handleBreadcrumbClick = (event, path) => {
    event.preventDefault();
    window.location.href = "/docTags";
  };
  const getContent = (res) => res?.data?.content || null;

  useEffect(() => {
    const saved = localStorage.getItem("aiBookSavedContents");
    if (saved) {
      const parsed = JSON.parse(saved).map((item) => ({
        ...item,
        formattedContent: null,
      }));
      setSavedContents(parsed);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("aiBookSavedContents", JSON.stringify(savedContents));
  }, [savedContents]);

  useEffect(() => {
    setEditedContent(generatedContent);
    if (generatedContent) {
      setFormattedContent(formatGeneratedContent(generatedContent));
      setPipelineContents((prev) => ({
        ...prev,
        [activeGenerator]: generatedContent,
      }));
    } else {
      setFormattedContent(null);
    }
  }, [generatedContent, activeGenerator]);

  const validateForm = () => {
    switch (activeGenerator) {
      case "idea":
        return formData.bookAbout && bookType && formData.category;
      case "outline":
        return formData.title && formData.chapters && bookType;
      case "cover":
        return formData.title && formData.summary && bookType;
      case "summary":
        return formData.bookAbout && bookType;
      default:
        return false;
    }
  };

  const getValidationMessage = () => {
    switch (activeGenerator) {
      case "idea":
        return "Please fill: Book About, Genre, and Category";
      case "outline":
        return "Please fill: Book Title, Chapters, and Genre";
      case "cover":
        return "Please fill: Book Title, Summary, and Genre";
      case "summary":
        return "Please fill: Book Content and Genre";
      default:
        return "Please fill all required fields!";
    }
  };

  const generators = [
    {
      id: "idea",
      label: "Book Ideas",
      description: "Generate unique book concepts",
      icon: <Lightbulb />,
      color: "#FF6B35",
      gradient: "linear-gradient(135deg, #FF6B35, #FFA62E)",
      bgColor: "rgba(255, 107, 53, 0.1)",
    },
    {
      id: "outline",
      label: "Book Outline",
      description: "Create chapter structures",
      icon: <FormatListBulleted />,
      color: "#4A90E2",
      gradient: "linear-gradient(135deg, #4A90E2, #357ABD)",
      bgColor: "rgba(74, 144, 226, 0.1)",
    },
    {
      id: "cover",
      label: "Cover Ideas",
      description: "Design book cover concepts",
      icon: <Palette />,
      color: "#44AF69",
      gradient: "linear-gradient(135deg, #44AF69, #2E8B57)",
      bgColor: "rgba(68, 175, 105, 0.1)",
    },
    {
      id: "summary",
      label: "Book Summary",
      description: "Craft summaries and synopses",
      icon: <DesignServices />,
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0, #7B1FA2)",
      bgColor: "rgba(156, 39, 176, 0.1)",
    },
  ];

  const pipelineOrder = ["idea", "outline", "cover", "summary"];

  const languages = [
    { value: "english", label: "English", flag: "🇺🇸" },
    { value: "tamil", label: "Tamil", flag: "🇮🇳" },
    { value: "arabic", label: "Arabic", flag: "🇸🇦" },
    { value: "spanish", label: "Spanish", flag: "🇪🇸" },
    { value: "french", label: "French", flag: "🇫🇷" },
    { value: "german", label: "German", flag: "🇩🇪" },
  ];

  const normalizeLanguageValue = (input) => {
    if (!input) return "english";
    const normalizedInput = String(input).trim().toLowerCase();
    const matchedLanguage = languages.find(
      (lang) =>
        lang.value.toLowerCase() === normalizedInput ||
        lang.label.toLowerCase() === normalizedInput,
    );
    return matchedLanguage?.value || "english";
  };

  const getLanguageLabel = (input) => {
    const normalizedValue = normalizeLanguageValue(input);
    return (
      languages.find((lang) => lang.value === normalizedValue)?.label ||
      "English"
    );
  };

  const bookTypesData = [
    { value: "fantasy", label: "Fantasy", icon: "🧙‍♂️" },
    { value: "scifi", label: "Sci-Fi", icon: "🚀" },
    { value: "romance", label: "Romance", icon: "❤️" },
    { value: "mystery", label: "Mystery", icon: "🕵️" },
    { value: "thriller", label: "Thriller", icon: "🔪" },
    { value: "horror", label: "Horror", icon: "👻" },
    { value: "historical", label: "Historical", icon: "🏛️" },
    { value: "biography", label: "Biography", icon: "📖" },
  ];

  const showNotification = (message, severity = "success") => {
    const normalizedMessage =
      typeof message === "string"
        ? message
        : message?.message ||
          message?.error ||
          message?.title ||
          "Something went wrong";
    setNotification({
      open: true,
      message: String(normalizedMessage),
      severity,
    });
  };

  const formatBoldText = (text) => {
    if (!text) return text;
    const parts = [];
    let currentIndex = 0;
    const boldRegex = /(\*\*|__)(.*?)\1/g;
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > currentIndex)
        parts.push(text.substring(currentIndex, match.index));
      parts.push(
        <Box
          component="span"
          key={`bold-${currentIndex}`}
          sx={{
            fontWeight: 700,
            color: generators.find((g) => g.id === activeGenerator)?.color,
            backgroundColor: alpha(
              generators.find((g) => g.id === activeGenerator)?.color,
              0.1,
            ),
            px: 0.8,
            py: 0.2,
            borderRadius: 1,
            mx: 0.3,
          }}
        >
          {match[2]}
        </Box>,
      );
      currentIndex = match.index + match[0].length;
    }
    if (currentIndex < text.length) parts.push(text.substring(currentIndex));
    return parts.length === 0 ? text : parts;
  };

  const formatGeneratedContent = (content) => {
    if (!content) return null;
    const lines = content.split("\n");
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={`br-${i}`} />;
      if (
        trimmed.endsWith(":") ||
        /^[A-Z][A-Za-z\s]*:$/.test(trimmed) ||
        trimmed.toLowerCase().includes("chapter") ||
        trimmed.toLowerCase().includes("title:") ||
        /^\d+\.\s/.test(trimmed)
      ) {
        return (
          <Box key={`title-${i}`} sx={{ mt: i > 0 ? 3 : 0, mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: generators.find((g) => g.id === activeGenerator)?.color,
                fontSize: "1.1rem",
                background: alpha(
                  generators.find((g) => g.id === activeGenerator)?.color,
                  0.08,
                ),
                p: 1.5,
                borderRadius: 2,
                borderLeft: `4px solid ${generators.find((g) => g.id === activeGenerator)?.color}`,
              }}
            >
              {trimmed}
            </Typography>
          </Box>
        );
      }
      if (
        trimmed.startsWith("-") ||
        trimmed.startsWith("•") ||
        trimmed.startsWith("*")
      ) {
        return (
          <Box
            key={`list-${i}`}
            sx={{ display: "flex", alignItems: "flex-start", mb: 1, ml: 2 }}
          >
            <Typography
              component="span"
              sx={{
                mr: 1.5,
                color: generators.find((g) => g.id === activeGenerator)?.color,
                fontWeight: 600,
              }}
            >
              •
            </Typography>
            <Typography component="span" sx={{ flex: 1, lineHeight: 1.6 }}>
              {formatBoldText(trimmed.replace(/^[-•*]\s*/, ""))}
            </Typography>
          </Box>
        );
      }
      return (
        <Typography
          key={`para-${i}`}
          paragraph
          sx={{ mb: 1.5, lineHeight: 1.7 }}
        >
          {formatBoldText(trimmed)}
        </Typography>
      );
    });
  };

  const parseOutlineChapters = (content) => {
    if (!content) return [];

    const lines = content.split(/\r?\n/);
    const headingRegex =
      /^(?:chapter\s+(?:\d+|[ivxlcdm]+|one|two|three|four|five|six|seven|eight|nine|ten)\b[\s:.\-)]*.*|\d+[\s:.\-)]+.+|[ivxlcdm]+[\s:.\-)]+.+)$/i;

    const sections = [];
    let current = null;

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed) {
        if (current) current.body.push("");
        return;
      }

      if (headingRegex.test(trimmed)) {
        if (current) sections.push(current);
        current = { heading: trimmed, body: [] };
        return;
      }

      if (!current) {
        current = { heading: "Book Overview", body: [] };
      }

      current.body.push(trimmed);
    });

    if (current) sections.push(current);

    return sections
      .map((section, index) => {
        const body = section.body.join("\n").trim();
        return {
          id: `outline-chapter-${index + 1}`,
          heading: section.heading || `Chapter ${index + 1}`,
          body,
          preview: body
            ? body.slice(0, 180)
            : "Expand to view this generated section.",
        };
      })
      .filter((section) => section.heading || section.body);
  };

  const parseCoverIdeas = (content) => {
    if (!content) return [];

    const lines = content.split(/\r?\n/);
    const headingRegex =
      /^(?:cover\s*(?:idea|concept|design)?\s*\d*\b.*|concept\s+\d+\b.*|design\s+\d+\b.*|\d+[\s:.\-)]+.+)$/i;

    const sections = [];
    let current = null;

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed) {
        if (current) current.body.push("");
        return;
      }

      if (headingRegex.test(trimmed)) {
        if (current) sections.push(current);
        current = { heading: trimmed, body: [] };
        return;
      }

      if (!current) {
        current = { heading: "Cover Concept 1", body: [] };
      }

      current.body.push(trimmed);
    });

    if (current) sections.push(current);

    return sections
      .map((section, index) => {
        const body = section.body.join("\n").trim();
        return {
          id: `cover-idea-${index + 1}`,
          heading: section.heading || `Cover Concept ${index + 1}`,
          body,
          preview: body
            ? body.slice(0, 180)
            : "Expand to view this generated cover idea.",
        };
      })
      .filter((section) => section.heading || section.body);
  };

  const outlineSource = editedContent || generatedContent;

  const outlineChapters = useMemo(
    () =>
      activeGenerator === "outline" ? parseOutlineChapters(outlineSource) : [],
    [activeGenerator, outlineSource],
  );

  const coverIdeas = useMemo(
    () => (activeGenerator === "cover" ? parseCoverIdeas(outlineSource) : []),
    [activeGenerator, outlineSource],
  );

  const toggleOutlineChapter = (chapterId) => {
    setExpandedOutlineChapters((prev) => ({
      ...prev,
      [chapterId]: !prev[chapterId],
    }));
  };

  const toggleCoverIdea = (ideaId) => {
    setExpandedCoverIdeas((prev) => ({
      ...prev,
      [ideaId]: !prev[ideaId],
    }));
  };

  const renderGeneratedPreview = () => {
    if (activeGenerator === "outline" && outlineChapters.length > 0) {
      return (
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 0.5, flexWrap: "wrap", gap: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={<LibraryBooks sx={{ fontSize: 16 }} />}
                label={`${outlineChapters.length} Chapters`}
                size="small"
                sx={{
                  fontWeight: 700,
                  color: "#357ABD",
                  bgcolor: alpha("#4A90E2", 0.09),
                  border: `1px solid ${alpha("#4A90E2", 0.22)}`,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Click the expand icon to open each generated chapter outline
              </Typography>
            </Stack>
            <Button
              size="small"
              onClick={() =>
                setExpandedOutlineChapters(
                  outlineChapters.reduce((acc, chapter) => {
                    acc[chapter.id] = true;
                    return acc;
                  }, {}),
                )
              }
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Expand All
            </Button>
          </Stack>

          {outlineChapters.map((chapter, index) => {
            const isOpen = !!expandedOutlineChapters[chapter.id];

            return (
              <Paper
                key={chapter.id}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  overflow: "hidden",
                  border: `1px solid ${alpha("#4A90E2", isOpen ? 0.28 : 0.14)}`,
                  background: isOpen
                    ? `linear-gradient(135deg, ${alpha("#4A90E2", 0.08)}, ${alpha("#ffffff", 0.92)})`
                    : alpha("#ffffff", 0.88),
                  transition: "all 0.28s ease",
                  boxShadow: isOpen
                    ? `0 10px 24px ${alpha("#4A90E2", 0.12)}`
                    : "0 2px 10px rgba(15,23,42,0.04)",
                }}
              >
                <Box
                  onClick={() => toggleOutlineChapter(chapter.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    cursor: "pointer",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        background: "linear-gradient(135deg, #4A90E2, #357ABD)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.82rem",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          color: "#1f3b73",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {chapter.heading}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {chapter.preview}
                      </Typography>
                    </Box>
                  </Stack>

                  <IconButton
                    size="small"
                    sx={{
                      color: "#357ABD",
                      bgcolor: alpha("#4A90E2", 0.1),
                      "&:hover": { bgcolor: alpha("#4A90E2", 0.16) },
                    }}
                  >
                    {isOpen ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={isOpen}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      pt: 0.25,
                      borderTop: `1px solid ${alpha("#4A90E2", 0.1)}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        background: alpha("#4A90E2", 0.05),
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.75,
                        fontSize: "0.94rem",
                      }}
                    >
                      {chapter.body || chapter.preview}
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      );
    }

    if (activeGenerator === "cover" && coverIdeas.length > 0) {
      return (
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 0.5, flexWrap: "wrap", gap: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={<Palette sx={{ fontSize: 16 }} />}
                label={`${coverIdeas.length} Cover Ideas`}
                size="small"
                sx={{
                  fontWeight: 700,
                  color: "#2E8B57",
                  bgcolor: alpha("#44AF69", 0.09),
                  border: `1px solid ${alpha("#44AF69", 0.22)}`,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Click the expand icon to open each generated cover concept
              </Typography>
            </Stack>
            <Button
              size="small"
              onClick={() =>
                setExpandedCoverIdeas(
                  coverIdeas.reduce((acc, idea) => {
                    acc[idea.id] = true;
                    return acc;
                  }, {}),
                )
              }
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Expand All
            </Button>
          </Stack>

          {coverIdeas.map((idea, index) => {
            const isOpen = !!expandedCoverIdeas[idea.id];

            return (
              <Paper
                key={idea.id}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  overflow: "hidden",
                  border: `1px solid ${alpha("#44AF69", isOpen ? 0.28 : 0.14)}`,
                  background: isOpen
                    ? `linear-gradient(135deg, ${alpha("#44AF69", 0.08)}, ${alpha("#ffffff", 0.92)})`
                    : alpha("#ffffff", 0.88),
                  transition: "all 0.28s ease",
                  boxShadow: isOpen
                    ? `0 10px 24px ${alpha("#44AF69", 0.12)}`
                    : "0 2px 10px rgba(15,23,42,0.04)",
                }}
              >
                <Box
                  onClick={() => toggleCoverIdea(idea.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    cursor: "pointer",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        background: "linear-gradient(135deg, #44AF69, #2E8B57)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.82rem",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          color: "#21543a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {idea.heading}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {idea.preview}
                      </Typography>
                    </Box>
                  </Stack>

                  <IconButton
                    size="small"
                    sx={{
                      color: "#2E8B57",
                      bgcolor: alpha("#44AF69", 0.1),
                      "&:hover": { bgcolor: alpha("#44AF69", 0.16) },
                    }}
                  >
                    {isOpen ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={isOpen}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      pt: 0.25,
                      borderTop: `1px solid ${alpha("#44AF69", 0.1)}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        background: alpha("#44AF69", 0.05),
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.75,
                        fontSize: "0.94rem",
                      }}
                    >
                      {idea.body || idea.preview}
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      );
    }

    return formattedContent;
  };

  useEffect(() => {
    if (activeGenerator !== "outline") return;

    if (outlineChapters.length === 0) {
      setExpandedOutlineChapters({});
      return;
    }

    setExpandedOutlineChapters((prev) => {
      const currentIds = new Set(outlineChapters.map((chapter) => chapter.id));
      const hasMatchingOpenChapter = Object.keys(prev).some((id) =>
        currentIds.has(id),
      );
      if (hasMatchingOpenChapter) return prev;
      return { [outlineChapters[0].id]: true };
    });
  }, [activeGenerator, outlineChapters]);

  useEffect(() => {
    if (activeGenerator !== "cover") return;

    if (coverIdeas.length === 0) {
      setExpandedCoverIdeas({});
      return;
    }

    setExpandedCoverIdeas((prev) => {
      const currentIds = new Set(coverIdeas.map((idea) => idea.id));
      const hasMatchingOpenIdea = Object.keys(prev).some((id) =>
        currentIds.has(id),
      );
      if (hasMatchingOpenIdea) return prev;
      return { [coverIdeas[0].id]: true };
    });
  }, [activeGenerator, coverIdeas]);

  // ── API Calls ─────────────────────────────────────────────
  const generateBookIdeaFromAPI = async () => {
    if (!formData.bookAbout || !bookType || !formData.category) {
      showNotification("Please fill all required fields!", "warning");
      return;
    }
    try {
      setGeneratorLoading("idea");
      setLoading(true);
      const res = await newBlogService.generateBookIdea({
        language: getLanguageLabel(language),
        book_genre: bookType,
        category: formData.category,
        book_about: formData.bookAbout,
        tone: "Creative",
      });
      const content = getContent(res);
      if (!content) {
        showNotification(
          res?.data?.message || "Failed to get content",
          "error",
        );
        return;
      }
      setGeneratedContent(content);
      showNotification("Book idea generated successfully!", "success");
    } catch (err) {
      showNotification("Failed to generate book idea", "error");
    } finally {
      setLoading(false);
      setGeneratorLoading(null);
    }
  };

  const generateBookCoverFromAPI = async () => {
    if (!formData.title || !formData.summary || !bookType) {
      showNotification("Please fill all required fields!", "warning");
      return;
    }
    try {
      setGeneratorLoading("cover");
      setLoading(true);
      const res = await newBlogService.generateBookCoverIdeas({
        language: getLanguageLabel(language),
        book_genre: bookType,
        chapters: formData.chapters || "12",
        book_title: formData.title,
        author_name: formData.author || "",
        subtitle: formData.subtitle || "",
        brief_summary: formData.summary,
        tone: "Creative",
      });
      const content = getContent(res);
      if (!content) {
        showNotification(res?.data?.message || "Failed", "error");
        return;
      }
      setGeneratedContent(content);
      showNotification("Book cover ideas generated!", "success");
    } catch (err) {
      showNotification("Failed to generate cover ideas", "error");
    } finally {
      setLoading(false);
      setGeneratorLoading(null);
    }
  };

  const generateBookOutlineFromAPI = async () => {
    if (!formData.title || !formData.chapters || !bookType) {
      showNotification("Please fill all required fields!", "warning");
      return;
    }
    try {
      setGeneratorLoading("outline");
      setLoading(true);
      const res = await newBlogService.generateBookOutline({
        language: getLanguageLabel(language),
        book_genre: bookType,
        chapters: formData.chapters,
        book_title: formData.title,
        target_audience: formData.audience || "General readers",
        tone: "Creative",
      });
      const content = getContent(res);
      if (!content) {
        showNotification(res?.data?.message || "Failed", "error");
        return;
      }
      setGeneratedContent(content);
      showNotification("Book outline generated!", "success");
    } catch (err) {
      showNotification("Failed to generate outline", "error");
    } finally {
      setLoading(false);
      setGeneratorLoading(null);
    }
  };

  const generateBookSummaryFromAPI = async () => {
    if (!formData.bookAbout || !bookType) {
      showNotification("Please fill all required fields!", "warning");
      return;
    }
    try {
      setGeneratorLoading("summary");
      setLoading(true);
      const res = await newBlogService.generateBookSummary({
        language: getLanguageLabel(language),
        book_genre: bookType,
        book_title: formData.title || "",
        book_about: formData.bookAbout,
        tone_of_writing: formData.tone || "Dramatic",
      });
      const content = getContent(res);
      if (!content) {
        showNotification(res?.data?.message || "Failed", "error");
        return;
      }
      setGeneratedContent(content);
      showNotification("Book summary generated!", "success");
    } catch (err) {
      showNotification("Failed to generate summary", "error");
    } finally {
      setLoading(false);
      setGeneratorLoading(null);
    }
  };

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (activeGenerator === "idea") await generateBookIdeaFromAPI();
    else if (activeGenerator === "outline") await generateBookOutlineFromAPI();
    else if (activeGenerator === "cover") await generateBookCoverFromAPI();
    else if (activeGenerator === "summary") await generateBookSummaryFromAPI();
  };

  const handleOpenSendDialog = (fromId, toId) => {
    setSendTarget({ from: fromId, to: toId });
    setSendDialogOpen(true);
  };

  const handleConfirmSend = () => {
    const { from, to } = sendTarget;
    const content = pipelineContents[from];
    const sourceFormData = stageFormData[from] || {};
    const targetFormData = stageFormData[to] || {};
    const sharedTitle =
      targetFormData.title ||
      sourceFormData.title ||
      extractTitleFromContent(content) ||
      "";
    let newFormData = { ...targetFormData };
    if (to === "outline")
      newFormData = {
        ...targetFormData,
        title: sharedTitle,
        plot: targetFormData.plot || content.substring(0, 500),
        chapters: targetFormData.chapters || sourceFormData.chapters || "",
        audience: targetFormData.audience || sourceFormData.audience || "",
      };
    else if (to === "cover")
      newFormData = {
        ...targetFormData,
        title: sharedTitle,
        chapters: targetFormData.chapters || sourceFormData.chapters || "",
        author: targetFormData.author || sourceFormData.author || "",
        subtitle: targetFormData.subtitle || sourceFormData.subtitle || "",
        summary: targetFormData.summary || content.substring(0, 500),
      };
    else if (to === "summary")
      newFormData = {
        ...targetFormData,
        title: sharedTitle,
        bookAbout: targetFormData.bookAbout || content.substring(0, 600),
        tone: targetFormData.tone || sourceFormData.tone || "",
        length: targetFormData.length || sourceFormData.length || "medium",
      };
    setSendDialogOpen(false);
    setActiveGenerator(to);
    setStageFormData((prev) => ({ ...prev, [to]: newFormData }));
    setFormData(newFormData);
    setGeneratedContent("");
    setEditedContent("");
    setFormattedContent(null);
    showNotification(
      `Content sent to ${generators.find((g) => g.id === to)?.label}!`,
      "success",
    );
  };

  const handleNewProject = () => {
    setActiveGenerator("idea");
    setFormData({});
    setStageFormData(createEmptyStageFormData());
    setGeneratedContent("");
    setEditedContent("");
    setFormattedContent(null);
    setBookType("");
    setLanguage("english");
    setPipelineContents({ idea: "", outline: "", cover: "", summary: "" });
    showNotification("Started new project!", "info");
  };

  const handleClearAll = () => {
    setFormData({});
    setStageFormData((prev) => ({ ...prev, [activeGenerator]: {} }));
    setGeneratedContent("");
    setEditedContent("");
    setFormattedContent(null);
    showNotification("All fields cleared!", "info");
  };

  const handleSave = () => {
    if (!editedContent.trim()) {
      showNotification("No content to save!", "warning");
      return;
    }
    const generatorLabel =
      generators.find((g) => g.id === activeGenerator)?.label || "Untitled";
    const newContent = {
      id: Date.now(),
      title: formData.title || generatorLabel,
      generatorLabel,
      content: editedContent,
      generator: activeGenerator,
      formData: { ...formData },
      language: getLanguageLabel(language),
      languageValue: normalizeLanguageValue(language),
      genre: bookType,
      timestamp: new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      preview: editedContent.substring(0, 80) + "...",
      color: generators.find((g) => g.id === activeGenerator)?.color,
    };
    setSavedContents([newContent, ...savedContents]);
    showNotification("Content saved successfully!", "success");
  };

  const handleCopy = () => {
    if (!editedContent.trim()) {
      showNotification("No content to copy!", "warning");
      return;
    }
    navigator.clipboard
      .writeText(editedContent)
      .then(() => showNotification("Copied!", "success"))
      .catch(() => showNotification("Failed to copy!", "error"));
  };

  const handleDownload = () => {
    if (!editedContent.trim()) {
      showNotification("No content to download!", "warning");
      return;
    }
    const element = document.createElement("a");
    const file = new Blob([editedContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `ai-book-${activeGenerator}-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    showNotification("Downloaded!", "success");
  };

  const handleViewSaved = (content) => {
    const restoredFormData = {
      ...(content.formData || {}),
      title: content.formData?.title || content.title || "",
    };
    setActiveGenerator(content.generator);
    setFormData(restoredFormData);
    setStageFormData((prev) => ({
      ...prev,
      [content.generator]: restoredFormData,
    }));
    setGeneratedContent(content.content);
    setEditedContent(content.content);
    setFormattedContent(formatGeneratedContent(content.content));
    setOpenSavedDialog(false);
    setLanguage(
      normalizeLanguageValue(content.languageValue || content.language),
    );
    setBookType(content.genre || "");
    showNotification("Content loaded!", "info");
  };

  const handleDeleteSaved = (id) => {
    setSavedContents(savedContents.filter((c) => c.id !== id));
    showNotification("Deleted!", "success");
  };

  const handleFormChange = (field) => (event) => {
    const nextFormData = { ...formData, [field]: event.target.value };
    setFormData(nextFormData);
    setStageFormData((prev) => ({ ...prev, [activeGenerator]: nextFormData }));
  };

  const handleGeneratorClick = (generatorId) => {
    setActiveGenerator(generatorId);
    setFormData(stageFormData[generatorId] || {});
    const existing = pipelineContents[generatorId];
    setGeneratedContent(existing || "");
    setEditedContent(existing || "");
    setFormattedContent(existing ? formatGeneratedContent(existing) : null);
  };

  const getGeneratorIcon = (generatorId) => {
    switch (generatorId) {
      case "idea":
        return <Lightbulb />;
      case "outline":
        return <FormatListBulleted />;
      case "cover":
        return <Palette />;
      case "summary":
        return <DesignServices />;
      default:
        return <AutoStories />;
    }
  };

  const fullBookFormData = {
    ...stageFormData.idea,
    ...stageFormData.outline,
    ...stageFormData.cover,
    ...stageFormData.summary,
    ...formData,
  };

  const renderFormFields = () => {
    const currentGenerator = generators.find((g) => g.id === activeGenerator);
    switch (activeGenerator) {
      case "idea":
        return (
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Card
                sx={{
                  border: `2px solid ${alpha(currentGenerator.color, 0.2)}`,
                  background: alpha(currentGenerator.color, 0.05),
                  borderRadius: 3,
                  mb: 2,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: currentGenerator.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Lightbulb sx={{ color: "white", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Book Idea Generator
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Generate unique book concepts based on your preferences
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="What is your book about?"
                placeholder="Describe your book idea, theme, or inspiration..."
                multiline
                rows={3}
                value={formData.bookAbout || ""}
                onChange={handleFormChange("bookAbout")}
                required
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <Description sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category || ""}
                  label="Category"
                  onChange={handleFormChange("category")}
                  required
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="fiction">📚 Fiction</MenuItem>
                  <MenuItem value="non-fiction">📊 Non-Fiction</MenuItem>
                  <MenuItem value="poetry">🎭 Poetry</MenuItem>
                  <MenuItem value="academic">🎓 Academic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Word Count"
                type="number"
                value={formData.wordCount || ""}
                onChange={handleFormChange("wordCount")}
                InputProps={{
                  endAdornment: (
                    <Typography variant="caption" sx={{ mr: 1 }}>
                      words
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      case "outline":
        return (
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Card
                sx={{
                  border: `2px solid ${alpha(currentGenerator.color, 0.2)}`,
                  background: alpha(currentGenerator.color, 0.05),
                  borderRadius: 3,
                  mb: 2,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: currentGenerator.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ViewList sx={{ color: "white", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Book Outline Generator
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create detailed chapter structures and plot outlines
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Book Title"
                value={formData.title || ""}
                onChange={handleFormChange("title")}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Chapters"
                value={formData.chapters || ""}
                onChange={handleFormChange("chapters")}
                InputProps={{ inputProps: { min: 1, max: 50 } }}
                required
              />
            </Grid>
            <Grid xs={12} md={3}>
              <TextField
                fullWidth
                label="Target Audience"
                value={formData.audience || ""}
                onChange={handleFormChange("audience")}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Plot Summary (Optional)"
                multiline
                rows={2}
                value={formData.plot || ""}
                onChange={handleFormChange("plot")}
                placeholder="Brief plot summary..."
              />
            </Grid>
          </Grid>
        );
      case "cover":
        return (
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Card
                sx={{
                  border: `2px solid ${alpha(currentGenerator.color, 0.2)}`,
                  background: alpha(currentGenerator.color, 0.05),
                  borderRadius: 3,
                  mb: 2,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: currentGenerator.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Image sx={{ color: "white", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Book Cover Ideas Generator
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create stunning book cover concepts and design ideas
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} md={4}>
              <TextField
                fullWidth
                label="Author's Name"
                value={formData.author || ""}
                onChange={handleFormChange("author")}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <TextField
                fullWidth
                label="Author's Name 2 (Optional)"
                value={formData.author2 || ""}
                onChange={handleFormChange("author2")}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <TextField
                fullWidth
                label="Book Title"
                value={formData.title || ""}
                onChange={handleFormChange("title")}
                required
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Subtitle (Optional)"
                value={formData.subtitle || ""}
                onChange={handleFormChange("subtitle")}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Brief Summary"
                multiline
                rows={3}
                value={formData.summary || ""}
                onChange={handleFormChange("summary")}
                required
                placeholder="Provide a brief summary for cover concept inspiration..."
              />
            </Grid>
          </Grid>
        );
      case "summary":
        return (
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Card
                sx={{
                  border: `2px solid ${alpha(currentGenerator.color, 0.2)}`,
                  background: alpha(currentGenerator.color, 0.05),
                  borderRadius: 3,
                  mb: 2,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: currentGenerator.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Summarize sx={{ color: "white", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Book Summary Generator
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Craft compelling summaries and synopses for your book
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Book Content"
                placeholder="Describe your book's main content, characters, and plot..."
                multiline
                rows={4}
                value={formData.bookAbout || ""}
                onChange={handleFormChange("bookAbout")}
                required
              />
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tone of Writing</InputLabel>
                <Select
                  value={formData.tone || ""}
                  label="Tone of Writing"
                  onChange={handleFormChange("tone")}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="formal">🎩 Formal</MenuItem>
                  <MenuItem value="casual">😊 Casual</MenuItem>
                  <MenuItem value="humorous">😂 Humorous</MenuItem>
                  <MenuItem value="dramatic">🎭 Dramatic</MenuItem>
                  <MenuItem value="poetic">📜 Poetic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Summary Length"
                select
                value={formData.length || "medium"}
                onChange={handleFormChange("length")}
              >
                <MenuItem value="short">Short (1-2 paragraphs)</MenuItem>
                <MenuItem value="medium">Medium (3-4 paragraphs)</MenuItem>
                <MenuItem value="detailed">Detailed (5+ paragraphs)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        py: 3,
        position: "relative",
      }}
    >
      {loading && <ModernLoadingOverlay />}

      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Breadcrumb */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                mr: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <img
                alt="logo"
                src={Poetlogopen}
                style={{
                  height: "28px",
                  width: "28px",
                  objectFit: "contain",
                  filter: "drop-shadow(0 0 7px rgba(163, 163, 164, 0.65))",
                }}
              />
            </Box>
            <Breadcrumbs
              separator={
                <NavigateNextIcon fontSize="small" sx={{ color: "#667eea" }} />
              }
              aria-label="breadcrumb"
            >
              <Box
                component={RouterLink}
                to="/docTags"
                onClick={(e) => handleBreadcrumbClick(e, "/docTags")}
                sx={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  color: "#667eea",
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(102, 126, 234, 0.08)",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.12)",
                    color: "#764ba2",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <AutoFixHigh sx={{ fontSize: 20, mr: 1 }} />
                MultiTool AI
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(102, 126, 234, 0.08)",
                }}
              >
                <AutoAwesome sx={{ fontSize: 20, color: "#667eea", mr: 1 }} />
                <Typography
                  sx={{
                    color: "#1a1a1a",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  AI Book Writing Tool
                </Typography>
              </Box>
            </Breadcrumbs>
          </Box>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 2, mt: -6, textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, #60a5fa, #a78bfa)"
                  : "linear-gradient(135deg, #3b82f6, #8b5cf6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 1,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            AI Book Writing Tool
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              fontWeight: 80,
              mb: 2,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Transform your ideas into compelling stories with advanced AI
            assistance
          </Typography>
        </Box>

        {/* ── GREETING BANNER ─────────────────────────────────── */}
        <GreetingBanner theme={theme} />

        {/* ── PIPELINE BAR ────────────────────────────────────── */}
        <BookPipelineBar
          generators={generators}
          pipelineContents={pipelineContents}
          activeGenerator={activeGenerator}
          onStepClick={handleGeneratorClick}
          onSendToNext={handleOpenSendDialog}
          onOpenFullBookV1={() => setOpenFullBookV1(true)}
          onOpenFullBookV2={() => setOpenFullBookV2(true)}
          onOpenAudioBook={() => setOpenAudioBook(true)}
          onOpenMovieScript={() => setOpenMovieScript(true)}
          theme={theme}
        />
        <MovieScriptComingSoon
          open={openMovieScript}
          onClose={() => setOpenMovieScript(false)}
          theme={theme}
        />
        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left Sidebar */}
          <Grid xs={12} md={3} lg={2.5}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                height: "fit-content",
                position: "sticky",
                top: 24,
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b, #0f172a)"
                    : "linear-gradient(135deg, #ffffff, #f8fafc)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "text.primary",
                }}
              >
                <Dashboard sx={{ color: "primary.main" }} />
                AI Tools
              </Typography>

              <Stack spacing={2}>
                {generators.map((gen, index) => {
                  const isCompleted = !!pipelineContents[gen.id];
                  const nextId = pipelineOrder[index + 1];
                  return (
                    <Box key={gen.id}>
                      <CardActionArea
                        onClick={() => handleGeneratorClick(gen.id)}
                        sx={{
                          borderRadius: 2.5,
                          overflow: "hidden",
                          position: "relative",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          border:
                            activeGenerator === gen.id
                              ? `2px solid ${gen.color}`
                              : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          background:
                            activeGenerator === gen.id
                              ? alpha(gen.color, 0.08)
                              : "transparent",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 6px 20px ${alpha(gen.color, 0.15)}`,
                            borderColor: gen.color,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                background: gen.gradient,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                flexShrink: 0,
                                position: "relative",
                              }}
                            >
                              {gen.icon}
                              {isCompleted && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -4,
                                    right: -4,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    bgcolor: "#44AF69",
                                    border: "2px solid white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CheckCircle
                                    sx={{ fontSize: 10, color: "white" }}
                                  />
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                  color:
                                    activeGenerator === gen.id
                                      ? gen.color
                                      : "text.primary",
                                }}
                              >
                                {gen.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {gen.description}
                              </Typography>
                            </Box>
                            {generatorLoading === gen.id && (
                              <CircularProgress
                                size={18}
                                sx={{ color: gen.color, flexShrink: 0 }}
                              />
                            )}
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                      {isCompleted && nextId && (
                        <Button
                          size="small"
                          variant="text"
                          endIcon={
                            <ArrowForward
                              sx={{ fontSize: "13px !important" }}
                            />
                          }
                          onClick={() => handleOpenSendDialog(gen.id, nextId)}
                          sx={{
                            mt: 0.5,
                            width: "100%",
                            py: 0.3,
                            fontSize: "0.68rem",
                            borderRadius: 1.5,
                            textTransform: "none",
                            color: generators.find((g) => g.id === nextId)
                              ?.color,
                            background: alpha(
                              generators.find((g) => g.id === nextId)?.color ||
                                "#000",
                              0.06,
                            ),
                            "&:hover": {
                              background: alpha(
                                generators.find((g) => g.id === nextId)
                                  ?.color || "#000",
                                0.12,
                              ),
                            },
                          }}
                        >
                          Send to{" "}
                          {generators.find((g) => g.id === nextId)?.label}
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {/* Quick Stats */}
              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="text.secondary"
                >
                  Quick Stats
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    {
                      label: "Saved Projects",
                      value: savedContents.length,
                      isChip: true,
                    },
                    { label: "AI Tools", value: generators.length },
                    { label: "Languages", value: languages.length },
                    {
                      label: "Pipeline Progress",
                      value: `${Object.values(pipelineContents).filter(Boolean).length}/${generators.length}`,
                    },
                  ].map((stat) => (
                    <Stack
                      key={stat.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {stat.label}
                      </Typography>
                      {stat.isChip ? (
                        <Chip
                          label={stat.value}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" fontWeight={600}>
                          {stat.value}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Main Content Panel */}
          <Grid xs={12} md={9} lg={9.5}>
            <Paper
              sx={{
                p: { xs: 2, md: 4 },
                borderRadius: 3,
                minHeight: "calc(100vh - 180px)",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b, #0f172a)"
                    : "linear-gradient(135deg, #ffffff, #f8fafc)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                position: "relative",
              }}
            >
              {/* Generator Header */}
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} md={8}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2.5,
                          background: generators.find(
                            (g) => g.id === activeGenerator,
                          )?.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          flexShrink: 0,
                        }}
                      >
                        {generators.find((g) => g.id === activeGenerator)?.icon}
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h5" fontWeight={700}>
                            {
                              generators.find((g) => g.id === activeGenerator)
                                ?.label
                            }
                          </Typography>
                          {pipelineContents[activeGenerator] && (
                            <Chip
                              icon={
                                <CheckCircle
                                  sx={{
                                    fontSize: "14px !important",
                                    color: "#44AF69 !important",
                                  }}
                                />
                              }
                              label="Completed"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.68rem",
                                bgcolor: alpha("#44AF69", 0.1),
                                color: "#44AF69",
                                border: `1px solid ${alpha("#44AF69", 0.3)}`,
                              }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {
                            generators.find((g) => g.id === activeGenerator)
                              ?.description
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      flexWrap="wrap"
                      gap={1.5}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => setOpenSavedDialog(true)}
                        startIcon={<History />}
                        size={isMobile ? "small" : "medium"}
                        sx={{ borderRadius: 2 }}
                      >
                        <Badge
                          badgeContent={savedContents.length}
                          color="primary"
                          sx={{ "& .MuiBadge-badge": { fontSize: 10 } }}
                        >
                          Saved
                        </Badge>
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (!validateForm()) {
                            showNotification(getValidationMessage(), "warning");
                            return;
                          }
                          setTimeout(() => handleGenerate(), 100);
                        }}
                        disabled={loading}
                        startIcon={<RocketLaunch />}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          borderRadius: 2,
                          background:
                            "linear-gradient(135deg, #4A90E2, #357ABD)",
                          boxShadow: "0 4px 16px rgba(74, 144, 226, 0.25)",
                          "&:hover": {
                            boxShadow: "0 6px 24px rgba(74, 144, 226, 0.35)",
                            background:
                              "linear-gradient(135deg, #357ABD, #2C6CB0)",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                          "Generate"
                        )}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Settings Bar */}
              <Paper
                sx={{
                  p: 2.5,
                  mb: 4,
                  borderRadius: 2.5,
                  background: alpha(theme.palette.background.paper, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                  backdropFilter: "blur(10px)",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Language sx={{ fontSize: 18 }} />
                          <Typography variant="body2">Language</Typography>
                        </Stack>
                      </InputLabel>
                      <Select
                        value={language}
                        label="Language"
                        onChange={(e) => setLanguage(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        {languages.map((lang) => (
                          <MenuItem key={lang.value} value={lang.value}>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                {lang.flag}
                              </Typography>
                              <Typography variant="body2">
                                {lang.label}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocalLibrary sx={{ fontSize: 18 }} />
                          <Typography variant="body2">Genre</Typography>
                        </Stack>
                      </InputLabel>
                      <Select
                        value={bookType}
                        label="Genre*"
                        onChange={(e) => setBookType(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">
                          <em>Select Genre</em>
                        </MenuItem>
                        {bookTypesData.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                {type.icon}
                              </Typography>
                              <Typography variant="body2">
                                {type.label}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Project Name"
                      placeholder="My Book Project..."
                      sx={{ borderRadius: 2 }}
                      InputProps={{
                        startAdornment: (
                          <EditNote
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 18,
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Form */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <EditNote sx={{ fontSize: 20, color: "primary.main" }} />
                  Configuration
                </Typography>
                {renderFormFields()}
              </Box>

              {/* Generated Content */}
              {generatedContent && (
                <Fade in>
                  <Box>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 2.5,
                        background:
                          theme.palette.mode === "dark"
                            ? alpha("#0f172a", 0.9)
                            : alpha("#ffffff", 0.95),
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                        position: "relative",
                        overflow: "hidden",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: generators.find(
                            (g) => g.id === activeGenerator,
                          )?.gradient,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          AI Generated Content
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <CustomTooltip title="View Raw Text">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setActiveTab(1);
                                setOpenContentDialog(true);
                              }}
                              sx={{
                                background: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                                "&:hover": {
                                  background: alpha(
                                    theme.palette.primary.main,
                                    0.2,
                                  ),
                                },
                              }}
                            >
                              <TextFields
                                fontSize="small"
                                sx={{ color: "#1e88e5" }}
                              />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Fullscreen">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setActiveTab(0);
                                setOpenContentDialog(true);
                              }}
                              sx={{
                                background: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                                "&:hover": {
                                  background: alpha(
                                    theme.palette.primary.main,
                                    0.2,
                                  ),
                                },
                              }}
                            >
                              <Fullscreen
                                fontSize="small"
                                sx={{ color: "#001aff" }}
                              />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Copy">
                            <IconButton
                              size="small"
                              onClick={handleCopy}
                              sx={{
                                background: alpha(
                                  theme.palette.success.main,
                                  0.1,
                                ),
                                "&:hover": {
                                  background: alpha(
                                    theme.palette.success.main,
                                    0.2,
                                  ),
                                },
                              }}
                            >
                              <ContentCopy
                                fontSize="small"
                                sx={{ color: "#2e7d32" }}
                              />
                            </IconButton>
                          </CustomTooltip>
                        </Stack>
                      </Stack>

                      <Box
                        sx={{
                          p: 2.5,
                          mb: 3,
                          borderRadius: 2,
                          background: alpha(
                            theme.palette.background.paper,
                            0.5,
                          ),
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          maxHeight: 400,
                          overflow: "auto",
                          fontFamily: "'Inter', 'Roboto', sans-serif",
                          fontSize: "0.95rem",
                          lineHeight: 1.7,
                        }}
                      >
                        {renderGeneratedPreview()}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        justifyContent="center"
                        sx={{ flexWrap: "wrap", gap: 1 }}
                      >
                        {[
                          {
                            icon: <Save />,
                            label: "Save",
                            action: handleSave,
                            gradient:
                              "linear-gradient(135deg, #44AF69, #2E8B57)",
                          },
                          {
                            icon: <Download />,
                            label: "Download",
                            action: handleDownload,
                            gradient:
                              "linear-gradient(135deg, #4A90E2, #357ABD)",
                          },
                          {
                            icon: <ContentCopy />,
                            label: "Copy",
                            action: handleCopy,
                            gradient:
                              "linear-gradient(135deg, #FFB347, #FFCC33)",
                          },
                          {
                            icon: <Share />,
                            label: "Share",
                            action: () => {},
                            gradient:
                              "linear-gradient(135deg, #9C27B0, #7B1FA2)",
                          },
                        ].map((btn) => (
                          <Button
                            key={btn.label}
                            variant="contained"
                            startIcon={btn.icon}
                            onClick={btn.action}
                            size="small"
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              background: btn.gradient,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              },
                            }}
                          >
                            {btn.label}
                          </Button>
                        ))}
                        {pipelineOrder.indexOf(activeGenerator) <
                          pipelineOrder.length - 1 &&
                          (() => {
                            const nextGen = generators.find(
                              (g) =>
                                g.id ===
                                pipelineOrder[
                                  pipelineOrder.indexOf(activeGenerator) + 1
                                ],
                            );
                            return (
                              <Button
                                variant="contained"
                                startIcon={<KeyboardDoubleArrowRight />}
                                onClick={() =>
                                  handleOpenSendDialog(
                                    activeGenerator,
                                    nextGen.id,
                                  )
                                }
                                size="small"
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                  background: nextGen.gradient,
                                  animation: "glow 2s ease-in-out infinite",
                                  "@keyframes glow": {
                                    "0%, 100%": {
                                      boxShadow: `0 2px 8px ${alpha(nextGen.color, 0.3)}`,
                                    },
                                    "50%": {
                                      boxShadow: `0 4px 20px ${alpha(nextGen.color, 0.6)}`,
                                    },
                                  },
                                }}
                              >
                                Send to {nextGen.label} →
                              </Button>
                            );
                          })()}
                      </Stack>
                    </Paper>
                  </Box>
                </Fade>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* FAB */}
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          "& .MuiSpeedDial-fab": {
            background: "linear-gradient(135deg, #4A90E2, #357ABD)",
            width: 56,
            height: 56,
            "&:hover": {
              background: "linear-gradient(135deg, #357ABD, #2C6CB0)",
            },
          },
          "& .MuiSpeedDialAction-staticTooltipLabel": {
            backgroundColor:
              theme.palette.mode === "light"
                ? `${theme.palette.common.white} !important`
                : `${theme.palette.grey[900]} !important`,
            color:
              theme.palette.mode === "light"
                ? `${theme.palette.primary.main} !important`
                : `${theme.palette.common.white} !important`,
            boxShadow: theme.shadows[6],
            fontSize: "10px",
            fontWeight: 500,
            borderRadius: "10px",
            padding: "8px 14px",
            backdropFilter: "blur(6px)",
            whiteSpace: "nowrap",
            wordBreak: "keep-all",
            minWidth: "max-content",
          },
        }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          icon={<PostAdd sx={{ color: "#1976d2" }} />}
          tooltipTitle="New Project"
          tooltipOpen
          tooltipPlacement="left"
          onClick={handleNewProject}
        />
        <SpeedDialAction
          icon={<Save sx={{ color: "#2e7d32" }} />}
          tooltipTitle="Save Content"
          tooltipOpen
          tooltipPlacement="left"
          onClick={handleSave}
        />
        <SpeedDialAction
          icon={<ClearAll sx={{ color: "#ef6c00" }} />}
          tooltipTitle="Clear All"
          tooltipOpen
          tooltipPlacement="left"
          onClick={handleClearAll}
        />
        <SpeedDialAction
          icon={<Headphones sx={{ color: "#ec4899" }} />}
          tooltipTitle="Audiobook"
          tooltipOpen
          tooltipPlacement="left"
          onClick={() => setOpenAudioBook(true)}
        />
      </SpeedDial>

      {/* Full Screen Content Dialog */}
      <Dialog
        open={openContentDialog}
        onClose={() => setOpenContentDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3, overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            background: generators.find((g) => g.id === activeGenerator)
              ?.gradient,
            color: "white",
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Content Preview
          </Typography>
          <IconButton
            onClick={() => setOpenContentDialog(false)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, pt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ minHeight: 40 }}
          >
            <Tab
              label="Formatted View"
              icon={<TextFields />}
              iconPosition="start"
              sx={{ minHeight: 40, fontSize: "0.875rem" }}
            />
            <Tab
              label="Raw Text"
              icon={<EditNote />}
              iconPosition="start"
              sx={{ minHeight: 40, fontSize: "0.875rem" }}
            />
          </Tabs>
        </Box>
        <DialogContent sx={{ py: 3, px: 3 }}>
          {activeTab === 0 ? (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.5),
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                lineHeight: 1.7,
                maxHeight: isMobile ? "calc(100vh - 300px)" : 500,
                overflow: "auto",
              }}
            >
              {renderGeneratedPreview()}
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 20 : 25}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                  border: "none",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pt: 2,
          }}
        >
          <Button
            onClick={handleCopy}
            startIcon={<ContentCopy />}
            variant="contained"
            color="primary"
          >
            Copy
          </Button>
          <Button
            onClick={handleDownload}
            startIcon={<Download />}
            variant="contained"
            color="success"
          >
            Download
          </Button>
          <Button
            onClick={handleSave}
            startIcon={<Save />}
            variant="contained"
            color="info"
          >
            Save
          </Button>
          <Button
            onClick={() => setOpenContentDialog(false)}
            startIcon={<Close />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saved Contents Dialog */}
      <Dialog
        open={openSavedDialog}
        onClose={() => setOpenSavedDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #1e293b, #0f172a)"
                : "linear-gradient(135deg, #ffffff, #f8fafc)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "#1565c0", display: "flex", alignItems: "center" }}
            >
              <History
                sx={{ mr: 1, verticalAlign: "middle", color: "#1976d2" }}
              />
              Saved Contents ({savedContents.length})
            </Typography>
            <IconButton
              onClick={() => setOpenSavedDialog(false)}
              size="small"
              sx={{
                color: "#d32f2f",
                "&:hover": { backgroundColor: alpha("#d32f2f", 0.1) },
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {savedContents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
              <History
                sx={{
                  fontSize: 60,
                  mb: 2,
                  opacity: 0.3,
                  color: "primary.main",
                }}
              />
              <Typography variant="h6" gutterBottom>
                No saved content yet
              </Typography>
              <Typography variant="body2">
                Generate some content and save it to see it here
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {savedContents.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    mb: 1.5,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: alpha(
                      item.color || theme.palette.primary.main,
                      0.03,
                    ),
                    transition: "all 0.2s",
                    "&:hover": {
                      background: alpha(
                        item.color || theme.palette.primary.main,
                        0.08,
                      ),
                      borderColor: alpha(
                        item.color || theme.palette.primary.main,
                        0.3,
                      ),
                      transform: "translateX(4px)",
                    },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <CustomTooltip title="View">
                        <IconButton
                          edge="end"
                          onClick={() => handleViewSaved(item)}
                          size="small"
                          sx={{
                            background: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            "&:hover": {
                              background: alpha(
                                theme.palette.primary.main,
                                0.2,
                              ),
                            },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </CustomTooltip>
                      <CustomTooltip title="Delete">
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteSaved(item.id)}
                          size="small"
                          sx={{
                            background: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            "&:hover": {
                              background: alpha(theme.palette.error.main, 0.2),
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </CustomTooltip>
                    </Stack>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(
                          item.color || theme.palette.primary.main,
                          0.1,
                        ),
                        color: item.color || theme.palette.primary.main,
                        width: 36,
                        height: 36,
                      }}
                    >
                      {getGeneratorIcon(item.generator)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight={600}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Box
                          sx={{
                            maxHeight: 60,
                            overflow: "hidden",
                            mb: 0.5,
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                            color: "text.secondary",
                          }}
                        >
                          {item.preview || item.content?.substring(0, 80)}
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Chip
                            label={getLanguageLabel(
                              item.languageValue || item.language,
                            )}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                          <Chip
                            label={item.genre}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AccessTime fontSize="inherit" />
                            {item.timestamp}
                          </Typography>
                        </Stack>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pt: 2,
          }}
        >
          <Button
            onClick={() => {
              localStorage.removeItem("aiBookSavedContents");
              setSavedContents([]);
              showNotification("All saved contents cleared!", "info");
            }}
            color="error"
            startIcon={<Delete />}
            disabled={savedContents.length === 0}
            variant="outlined"
          >
            Clear All
          </Button>
          <Button
            onClick={() => {
              const dataStr = JSON.stringify(savedContents, null, 2);
              const dataUri =
                "data:application/json;charset=utf-8," +
                encodeURIComponent(dataStr);
              const el = document.createElement("a");
              el.setAttribute("href", dataUri);
              el.setAttribute("download", `ai-book-saved-${Date.now()}.json`);
              el.click();
              showNotification("Exported!", "success");
            }}
            startIcon={<Download />}
            disabled={savedContents.length === 0}
            variant="contained"
            color="primary"
          >
            Export All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send-to-Next Dialog */}
      <SendToNextDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        fromGenerator={sendTarget.from}
        toGenerator={sendTarget.to}
        content={pipelineContents[sendTarget.from] || ""}
        onConfirm={handleConfirmSend}
        generators={generators}
      />

      {/* ── V1 Full Book — Pipeline Viewer ── */}
      <FullBookScenario
        open={openFullBookV1}
        onClose={() => setOpenFullBookV1(false)}
        pipelineContents={pipelineContents}
        generators={generators}
        bookType={bookType}
        language={getLanguageLabel(language)}
      />

      {/* ── V2 Full Book — AI E-Book Generator ── */}
      <E_BookScenario
        open={openFullBookV2}
        onClose={() => setOpenFullBookV2(false)}
        pipelineContents={pipelineContents}
        generators={generators}
        bookType={bookType}
        language={getLanguageLabel(language)}
        bookTitle={fullBookFormData.title || ""}
        chapterCount={fullBookFormData.chapters || 5}
        authorName={fullBookFormData.author || ""}
        authorName2={fullBookFormData.author2 || ""}
        tone={fullBookFormData.tone || ""}
      />

      {/* ── Audiobook Script Generator ── */}
      <BlogAIAudioBook
        open={openAudioBook}
        onClose={() => setOpenAudioBook(false)}
        pipelineContents={pipelineContents}
        generators={generators}
        bookType={bookType}
        language={language.charAt(0).toUpperCase() + language.slice(1)}
      />

      {/* Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={notification.severity}
            onClose={() => setNotification({ ...notification, open: false })}
            sx={{
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              color: "white !important",
              fontWeight: 500,
              "&.MuiAlert-filledSuccess": {
                backgroundColor: "#2e7d32 !important",
              },
              "&.MuiAlert-filledError": {
                backgroundColor: "#d32f2f !important",
              },
              "&.MuiAlert-filledWarning": {
                backgroundColor: "#ed6c02 !important",
              },
              "&.MuiAlert-filledInfo": {
                backgroundColor: "#0288d1 !important",
              },
              "& .MuiAlert-icon": { color: "white !important" },
              "& .MuiAlert-message": {
                color: "white !important",
                fontWeight: 500,
                padding: "8px 0",
              },
            }}
          >
            {notification.message}
          </MuiAlert>
        </Box>
      </Snackbar>
    </Box>
  );
};

export default AIBookWriting;
